// src/pages/holding/HoldingPage.tsx
import React from "react";

import background from "../../assets/holding/bg.webp";
import logo from "../../assets/holding/logo.svg";
import molecule42 from "../../assets/holding/molecule-4-2.webp";
import molecule51 from "../../assets/holding/molecule-5-1.webp";
import molecule102 from "../../assets/holding/molecule-10-2.webp";
import molecule111 from "../../assets/holding/molecule-11-1.webp";
import molecule112 from "../../assets/holding/molecule-11-2.webp";

/**
 * The public page at `/` until launch, from the holding page frames (desktop 1440 x 900,
 * mobile 390 x 844). It goes when the domain moves to the new site.
 *
 * The form posts straight to Mailchimp's hosted form, so there is no key and no backend.
 * Submitting is the opt-in: the approved wording sits under the field and nothing is
 * pre-ticked. The tags record the opt-in and the wording version shown, the same three the
 * new site's sign-up writes.
 */

export const CONSENT_WORDING_VERSION = "2026-10-02";
export const CONSENT_WORDING =
  "I would like to receive emails from Hysteron about women's health. These emails tell " +
  "Hysteron when they are opened and which links are clicked, so we can see what is useful. " +
  "I can unsubscribe at any time.";

export type MailchimpConfig = {
  action: string;
  tags: string;
  honeypot: string;
};

/**
 * The hosted form's action URL (it carries the `u` and `id` values) and the tag ids for
 * `newsletter`, `opt-in-consent` and `consent-wording:2026-10-02`. Read at build time from
 * REACT_APP_MAILCHIMP_ACTION, REACT_APP_MAILCHIMP_TAG_NEWSLETTER,
 * REACT_APP_MAILCHIMP_TAG_OPT_IN_CONSENT and REACT_APP_MAILCHIMP_TAG_CONSENT_WORDING, so the
 * values drop in through the env files.
 *
 * Null unless all of it is there: an https URL with both `u` and `id`, and all three tags.
 * A sign-up without the consent tags would lose the record of the opt-in and the wording
 * shown, so the form stays off rather than send one.
 */
export function mailchimpConfig(env: Record<string, string | undefined> = process.env): MailchimpConfig | null {
  const action = (env.REACT_APP_MAILCHIMP_ACTION ?? "").trim();
  if (!action) return null;

  let url: URL;
  try {
    url = new URL(action);
  } catch {
    return null;
  }
  const u = url.searchParams.get("u") ?? "";
  const id = url.searchParams.get("id") ?? "";
  if (url.protocol !== "https:" || !u || !id) return null;

  const tags = [
    env.REACT_APP_MAILCHIMP_TAG_NEWSLETTER,
    env.REACT_APP_MAILCHIMP_TAG_OPT_IN_CONSENT,
    env.REACT_APP_MAILCHIMP_TAG_CONSENT_WORDING,
  ].map((tag) => (tag ?? "").trim());
  if (tags.some((tag) => !tag)) return null;

  // Mailchimp's own bot field: a real visitor never sees or fills it.
  return { action, tags: tags.join(","), honeypot: `b_${u}_${id}` };
}

type Molecule = {
  src: string;
  /** Centre, as a share of the frame's width and height. */
  x: number;
  y: number;
  /** Width as a share of the frame's width, and width over height. */
  width: number;
  aspect: number;
  rotate: number;
  opacity: number;
  /** For an image cropped inside its box: the image's size and offset in the box. */
  crop?: { width: string; height: string; left: string; top: string };
};

const CROP_51 = { width: "248.24%", height: "127.37%", left: "-137.7%", top: "-10.22%" };
const CROP_42 = { width: "192.66%", height: "113.45%", left: "0", top: "0" };

// Positions from the 1440 x 900 frame.
const DESKTOP_MOLECULES: Molecule[] = [
  { src: molecule111, x: 1061.6 / 1440, y: 332.6 / 900, width: 891.017 / 1440, aspect: 891.017 / 1113.703, rotate: -107.4, opacity: 0.72 },
  { src: molecule51, x: 292.5 / 1440, y: 183.5 / 900, width: 685 / 1440, aspect: 685 / 751, rotate: 0, opacity: 0.4, crop: CROP_51 },
  { src: molecule42, x: 212.16 / 1440, y: 711.23 / 900, width: 309.541 / 1440, aspect: 309.541 / 295.564, rotate: -128.82, opacity: 0.5, crop: CROP_42 },
  { src: molecule102, x: 843.56 / 1440, y: 249.87 / 900, width: 292.413 / 1440, aspect: 292.413 / 365.495, rotate: 107.71, opacity: 0.7 },
  { src: molecule112, x: 1287.49 / 1440, y: 682.26 / 900, width: 194.68 / 1440, aspect: 194.68 / 243.335, rotate: 160.42, opacity: 1 },
];

// Positions from the 390 x 844 frame.
const MOBILE_MOLECULES: Molecule[] = [
  { src: molecule51, x: 56.8 / 390, y: 42.65 / 844, width: 205.594 / 390, aspect: 205.594 / 225.3, rotate: 0, opacity: 0.18, crop: CROP_51 },
  { src: molecule111, x: 333.44 / 390, y: 142.5 / 844, width: 267.305 / 390, aspect: 267.305 / 334.111, rotate: -107.4, opacity: 0.48 },
  { src: molecule102, x: 57.47 / 390, y: 626.68 / 844, width: 179.235 / 390, aspect: 179.235 / 224.031, rotate: -135.38, opacity: 0.5 },
];

const pct = (n: number) => `${(n * 100).toFixed(3)}%`;

const MoleculeLayer: React.FC<{ molecules: Molecule[]; className: string }> = ({ molecules, className }) => (
  <div aria-hidden="true" className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}>
    {molecules.map((m, i) => (
      <div
        key={i}
        className="absolute overflow-hidden"
        style={{
          left: pct(m.x),
          top: pct(m.y),
          width: pct(m.width),
          aspectRatio: String(m.aspect),
          opacity: m.opacity,
          transform: `translate(-50%, -50%) rotate(${m.rotate}deg)`,
        }}
      >
        {m.crop ? (
          <img alt="" src={m.src} className="absolute max-w-none" style={m.crop} />
        ) : (
          <img alt="" src={m.src} className="absolute inset-0 h-full w-full object-cover" />
        )}
      </div>
    ))}
  </div>
);

const PILLS = ["Built for menopause", "Venous blood, not a finger prick", "Results in three working days"];

// The phone frame shortens the placeholder so it fits beside the button.
const NARROW = "(max-width: 767px)";
function useNarrow(): boolean {
  const [narrow, setNarrow] = React.useState(() => window.matchMedia?.(NARROW).matches ?? false);
  React.useEffect(() => {
    const query = window.matchMedia?.(NARROW);
    if (!query) return;
    const onChange = () => setNarrow(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return narrow;
}

// Until the hosted form is configured the field and button are disabled, so a visitor
// cannot type an address that goes nowhere.
const SignupForm: React.FC = () => {
  const config = mailchimpConfig();
  const narrow = useNarrow();

  return (
    <form
      action={config?.action}
      method="post"
      target="_blank"
      rel="noopener"
      aria-label="Email sign-up"
      onSubmit={(event) => {
        if (!config) event.preventDefault();
      }}
      aria-disabled={config ? undefined : true}
      className="w-full md:max-w-[520px]"
    >
      <div className="flex w-full items-center gap-3 rounded-full border border-[#f8f5ee] bg-[#1c2857] py-[6px] pl-5 pr-[6px] md:pl-7">
        <label htmlFor="holding-email" className="sr-only">
          Email address
        </label>
        <input
          id="holding-email"
          type="email"
          name="EMAIL"
          required
          autoComplete="email"
          inputMode="email"
          disabled={!config}
          aria-describedby="holding-consent"
          placeholder={narrow ? "Your email" : "Your email address"}
          className="min-w-0 flex-1 bg-transparent font-ui text-[14px] font-medium text-[#f8f5ee] placeholder:text-[#e0aed0] focus:outline-none md:text-[15px]"
        />
        {config ? <input type="hidden" name="tags" value={config.tags} /> : null}
        {config ? (
          <div aria-hidden="true" className="absolute left-[-5000px]">
            <input type="text" name={config.honeypot} tabIndex={-1} defaultValue="" />
          </div>
        ) : null}
        <button
          type="submit"
          disabled={!config}
          aria-describedby="holding-consent"
          className="shrink-0 whitespace-nowrap rounded-full bg-[#fae8ac] px-5 py-[15px] font-ui text-[14px] font-semibold leading-none text-[#1c2857] transition-opacity hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#fae8ac] md:px-7 md:py-[17px]"
        >
          Keep me posted <span aria-hidden="true">→</span>
        </button>
      </div>
      <p
        id="holding-consent"
        data-consent-wording={CONSENT_WORDING_VERSION}
        className="mt-4 font-ui text-[13px] font-medium leading-normal text-[#e0aed0]"
      >
        {CONSENT_WORDING}
      </p>
    </form>
  );
};

const HoldingPage: React.FC = () => (
  <div className="relative flex min-h-screen flex-col overflow-hidden bg-[#1c2857] text-[#f8f5ee]">
    <img
      alt=""
      aria-hidden="true"
      src={background}
      className="pointer-events-none absolute inset-0 h-full w-full object-cover opacity-60"
    />
    <MoleculeLayer molecules={MOBILE_MOLECULES} className="md:hidden" />
    <MoleculeLayer molecules={DESKTOP_MOLECULES} className="hidden md:block" />

    <header className="relative px-6 pt-7 md:px-[10.28%] md:pt-12">
      <img src={logo} alt="Hysteron" className="h-auto w-[112.5px] md:w-[150px]" />
    </header>

    <main className="relative flex-1 px-6 pb-12 pt-[94px] md:px-[10.28%] md:pt-[141px]">
      <h1 className="font-display text-[clamp(56px,18.5vw,72px)] font-normal leading-[0.98] md:max-w-[720px] md:text-[90px]">
        We’ll be back <em className="italic">soon.</em>
      </h1>
      <p className="mt-6 font-ui text-[16px] font-medium leading-[1.5] md:mt-7 md:max-w-[560px] md:text-[18px]">
        We’ve been improving Hysteron, so you can see the full picture. Check back on 20th October,
        when we launch Hysteron 2.0.
      </p>
      <div className="mt-7 md:mt-10">
        <SignupForm />
      </div>
    </main>

    <footer className="relative border-t border-[rgba(224,174,208,0.45)] px-[10px] pb-10 pt-8 md:py-[18px]">
      <ul className="mx-auto flex justify-center gap-[5px] md:gap-6 lg:gap-[74px]">
        {PILLS.map((pill) => (
          <li
            key={pill}
            className="flex h-[50px] min-w-0 max-w-[120px] flex-1 items-center justify-center rounded-full border border-[#e0aed0] px-[6px] text-center font-ui text-[10px] uppercase leading-[1.4] text-[#f8f5ee] backdrop-blur-[2px] md:h-[52px] md:max-w-[250px] md:px-3 md:text-[13px]"
          >
            {pill}
          </li>
        ))}
      </ul>
    </footer>
  </div>
);

export default HoldingPage;
